/**
 * Copyright 2020 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import styles from './BlogProductCard.module.scss';
import WarrantyBadge from '@/components/AZCustomComponent/WarrantyBadge';
import cx from 'classnames';
import { useLabels } from '@/hooks/useLabels';
import { View, type ViewProps, Text, Icon, ArrowRight } from '@az/starc-ui';
import type {
  FormattedContentStackBlogProductGrid,
  FormattedContentStackBlogProductGridProductCard,
} from '@/features/contentstack/interface/models';
import type { ContentStackProductGrid } from '@/features/contentstack/interface';
import { CMSImage } from '@/features/contentstack/components/CMSImage/CMSImage';
import { CMSLink } from '@/features/contentstack/components/CMSLink/CMSLink';
import { CMSButton } from '@/features/contentstack/components/CMSButton/CMSButton';
import { usePageSpecificData } from '@/utils/analytics/usePageSpecificData';

type CSProduct = NonNullable<FormattedContentStackBlogProductGridProductCard>[0];

export type BlogProductCardProps = {
  product: CSProduct;
  detailsAlignment?: ContentStackProductGrid['product_details_alignment'] | null;
  headerTextStyle?: NonNullable<FormattedContentStackBlogProductGrid>['header_text_style'];
  mobileTwoCardSplit?: boolean;
};

const warrantyText: Record<NonNullable<CSProduct['warranty']>, string> = {
  One: '1',
  Two: '2',
  Three: '3',
  Four: '4',
  Five: '5',
  Six: '6',
  Ten: '10',
  Lifetime: 'Limited-Lifetime Warranty',
};

const classNameForDetailAlignment: Record<
  NonNullable<ContentStackProductGrid['product_details_alignment']>,
  string
> = {
  Left: styles.contentLeftAligned,
  Center: styles.contentCenterAligned,
};

const labelMap = {
  WarrantyLabel: 'label_MyWarrantiesDetails_YearWarranty',
};

export const BlogProductCard = ({
  product,
  detailsAlignment,
  headerTextStyle,
  mobileTwoCardSplit,
}: BlogProductCardProps) => {
  const labels = useLabels(labelMap);
  const yearWarrantyLabel = labels.WarrantyLabel;
  const { setPageSpecificData } = usePageSpecificData();
  const trackAnalytics = () => {
    setPageSpecificData({
      productFindingMethod: 'Internal Campaign',
      internalCampaignInteraction: 'internalCampaignInteraction',
      pfmLocation: 'Blogs Page',
    });
  };

  const resolvedDetailsAlignment = detailsAlignment ?? 'Center';
  const buttonAlignment: ViewProps<'div'>['justify'] =
    resolvedDetailsAlignment === 'Center' ? 'center' : 'start';
  const maybeLinkChildren = (
    <>
      {product.header_above_image && (
        <Text
          className={
            headerTextStyle === 'AutoZone Condensed Blog Header'
              ? styles.blogTitle1
              : styles.blogTitle2
          }
        >
          {product.product_name}
        </Text>
      )}
      <View className={cx(styles.image, { [styles.mobileTwoCardSplit]: mobileTwoCardSplit })}>
        {product?.image?.image_url && (
          <CMSImage
            image={{
              image_url: product?.image?.image_url,
              image_alt_text: product?.image?.image_alt_text,
            }}
          />
        )}
      </View>
      {!product.header_above_image && (
        <Text
          className={
            headerTextStyle === 'AutoZone Condensed Blog Header'
              ? styles.blogTitle1
              : styles.blogTitle2
          }
        >
          {product.product_name}
        </Text>
      )}
    </>
  );

  return (
    <View
      className={`${styles.productCardContent} ${classNameForDetailAlignment[resolvedDetailsAlignment]}`}
    >
      {product.link ? (
        <CMSLink className={styles.productCardLink} to={product.link} handleClick={trackAnalytics}>
          {maybeLinkChildren}
        </CMSLink>
      ) : (
        <View className={styles.productCardLink}>{maybeLinkChildren}</View>
      )}
      {product.product_description ? (
        <Text data-testid="product-card-description">{product.product_description}</Text>
      ) : null}
      {product.warranty ? (
        <View className={styles.warrantySpace}>
          <WarrantyBadge
            warranty={
              product.warranty === 'Lifetime'
                ? `${warrantyText[product.warranty]}`
                : `${warrantyText[product.warranty]}${yearWarrantyLabel}`
            }
            classes={{ root: styles.warranty }}
          />
        </View>
      ) : null}
      {product?.button?.theme && product.button.link?.href && product.button.link?.title ? (
        <View className={styles.buttonPadding}>
          <CMSButton
            options={{ theme: product?.button?.theme, link: product?.button?.link }}
            justify={buttonAlignment}
            textClassName={styles.buttonText}
            size="large"
            onAnalyticsClick={trackAnalytics}
            ariaLabel={`${product.button.link.title} ${product.product_name}`}
          />
        </View>
      ) : null}
      {product.cta_link_active && (
        <View
          padding={[4, 0, 0, 0]}
          direction="row"
          justify="start"
          align="center"
          className={cx(styles.linkButton)}
        >
          <CMSLink to={product?.cta_link?.href ?? '#'} handleClick={trackAnalytics}>
            {product.cta_link?.title}
            <Icon size={5} svg={ArrowRight} />
          </CMSLink>
        </View>
      )}
    </View>
  );
};
